(function () {
  'use strict';

  if (window.PellairLoader && window.PellairLoader.version === 'v28') return;

  const html = document.documentElement;
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tokens = new Set();
  let root = null;
  let reveal = null;
  let progress = 0;
  let target = 0;
  let frame = 0;
  let lastTime = 0;
  let finishTimer = 0;
  let fadeTimer = 0;
  let safetyTimer = 0;
  let startedAt = 0;
  let tokenSeed = 0;
  let completing = false;
  const minVisible = reduceMotion ? 120 : 560;
  const maxVisible = reduceMotion ? 2400 : 7200;

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function now() {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
  }

  function ensure() {
    if (root || !document.body) return root;
    root = document.createElement('div');
    root.className = 'pellair-loading-bar';
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = '<div class="pellair-loading-bar__track"><div class="pellair-loading-bar__reveal"><div class="pellair-loading-bar__texture"></div><div class="pellair-loading-bar__inner-glow"></div></div><span class="pellair-loading-bar__shine"></span><span class="pellair-loading-bar__head"></span></div>';
    document.body.appendChild(root);
    reveal = root.querySelector('.pellair-loading-bar__reveal');
    if (window.PELLAIR_LOADER_TEXTURE_URL) {
      root.style.setProperty('--pellair-loader-texture', 'url("' + String(window.PELLAIR_LOADER_TEXTURE_URL).replace(/"/g, '\\"') + '")');
    }
    render();
    return root;
  }

  function render() {
    if (!root) return;
    const safe = clamp(progress, 0.001, 1);
    root.style.setProperty('--pellair-loader-percent', safe.toFixed(5));
    root.style.setProperty('--pellair-loader-width', (safe * 100).toFixed(3) + '%');
    root.style.setProperty('--pellair-loader-clip', (100 - safe * 100).toFixed(3) + '%');
    root.style.setProperty('--pellair-loader-x', clamp(safe * 100, 4, 100).toFixed(3) + '%');
    if (reveal) reveal.style.removeProperty('width');
  }

  function fullyHide() {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    if (!root) return;
    root.classList.remove('is-visible', 'is-hiding', 'is-complete');
    html.classList.remove('pellair-loading-active');
    tokens.clear();
    progress = 0;
    target = 0;
    startedAt = 0;
    completing = false;
    lastTime = 0;
    render();
    root.hidden = true;
  }

  function animate(timestamp) {
    frame = 0;
    if (!root || root.hidden) return;
    if (!lastTime) lastTime = timestamp;
    const delta = clamp(timestamp - lastTime, 0, 80);
    lastTime = timestamp;

    if (tokens.size && !completing) {
      const elapsed = startedAt ? timestamp - startedAt : 0;
      const natural = 0.14 + (1 - Math.exp(-elapsed / 1450)) * 0.69;
      target = Math.min(0.84, Math.max(target, natural));
    }

    const speed = completing ? 82 : 175;
    const alpha = reduceMotion ? 1 : 1 - Math.exp(-delta / speed);
    progress += (target - progress) * alpha;
    if (Math.abs(target - progress) < 0.0008) progress = target;
    render();

    if (completing && progress >= 0.998) {
      progress = 1;
      render();
      root.classList.add('is-complete');
      window.clearTimeout(fadeTimer);
      fadeTimer = window.setTimeout(function () {
        if (!root) return;
        root.classList.remove('is-visible', 'is-complete');
        root.classList.add('is-hiding');
        html.classList.remove('pellair-loading-active');
        fadeTimer = window.setTimeout(fullyHide, reduceMotion ? 70 : 280);
      }, reduceMotion ? 90 : 300);
      return;
    }

    if (tokens.size || completing || Math.abs(target - progress) > 0.001) {
      frame = window.requestAnimationFrame(animate);
    } else {
      lastTime = 0;
    }
  }

  function requestLoop() {
    if (frame || !root || root.hidden) return;
    lastTime = 0;
    frame = window.requestAnimationFrame(animate);
  }

  function show() {
    ensure();
    if (!root || html.classList.contains('intro-pending')) return;
    window.clearTimeout(fadeTimer);
    window.clearTimeout(finishTimer);
    root.hidden = false;
    root.classList.remove('is-hiding', 'is-complete');
    html.classList.add('pellair-loading-active');
    window.requestAnimationFrame(function () {
      if (root && !root.hidden) root.classList.add('is-visible');
    });
    requestLoop();
  }

  function start() {
    ensure();
    const token = 'pellair-load-' + (++tokenSeed);
    tokens.add(token);
    completing = false;
    startedAt = startedAt || now();
    progress = Math.max(progress, 0.14);
    target = Math.max(target, 0.22);
    show();
    render();
    requestLoop();
    window.clearTimeout(safetyTimer);
    safetyTimer = window.setTimeout(function () { done(true); }, maxVisible);
    return token;
  }

  function done(tokenOrForce) {
    if (tokenOrForce === true) tokens.clear();
    else if (typeof tokenOrForce === 'string') tokens.delete(tokenOrForce);
    else {
      const first = tokens.values().next().value;
      if (first) tokens.delete(first);
    }
    if (tokens.size) return;
    window.clearTimeout(safetyTimer);
    if (!root || root.hidden) {
      fullyHide();
      return;
    }
    const elapsed = startedAt ? now() - startedAt : minVisible;
    window.clearTimeout(finishTimer);
    finishTimer = window.setTimeout(function () {
      completing = true;
      target = 1;
      requestLoop();
    }, Math.max(0, minVisible - elapsed));
  }

  function internalNavigation(link) {
    if (!link || !link.href || link.target && link.target !== '_self' || link.hasAttribute('download')) return false;
    const raw = link.getAttribute('href') || '';
    if (!raw || /^(?:#|mailto:|tel:|javascript:)/i.test(raw)) return false;
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return false;
      return true;
    } catch (error) {
      return false;
    }
  }

  window.PellairLoader = { version: 'v28', start: start, done: done, ensure: ensure };
  window.PellairLoaderTest = function (duration) {
    const token = start();
    window.setTimeout(function () { done(token); }, Number(duration) || 1600);
  };

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href]');
    if (internalNavigation(link)) start();
  }, true);

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.matches('[data-product-form]')) return;
    start();
  }, true);

  document.addEventListener('pellair:intro-complete', function () { done(true); });
  window.addEventListener('pageshow', function () { done(true); });
  window.addEventListener('pagehide', function () { done(true); });

  if (!html.classList.contains('intro-pending') && document.readyState !== 'complete') {
    const initialToken = start();
    window.addEventListener('load', function () { done(initialToken); }, { once: true });
  }
})();
